/**
 * Live Code Preview — terminal-style window that renders the partial generator output.
 * Collapsible. Calls getGenerator(cliType)(payload) in a try/catch so partial state
 * doesn't crash the panel.
 */
import React, { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Copy, Download, FileCode2 } from "lucide-react";
import { getGenerator, getOutputFilename } from "../generators";
import { toast } from "sonner";

/** Hand-rolled lightweight syntax highlighter for FortiOS-ish keywords. */
function highlight(line) {
    if (line.startsWith("#") || line.startsWith("!") || line.startsWith("<!--")) {
        return <span className="text-accent-amber/70">{line}</span>;
    }
    // tokenize: leading whitespace, keywords, strings, numbers, comments
    const parts = [];
    const re = /(\s+)|("(?:[^"\\]|\\.)*")|(\b(?:config|edit|next|end|set|unset|delete|purge|exit|configure|interface|vlan|policy|alias|address-object|address-group|service-object|service-group|secure-policy|firewall|network|profile|content-filter|category-action|management|description|range|hostname|timezone|username|password|gateway|dns|enable|disable|trusted|optional|external|custom|allow|deny|reject|pass|tcp|udp|inet|exit|aliases|laggs|vlans|interfaces|gateways|dhcpd|filter|nat|outbound|system|webgui|user|group)\b)|(\d+(?:\.\d+){0,3}(?:\/\d+)?)/g;
    let last = 0;
    let m;
    while ((m = re.exec(line)) !== null) {
        if (m.index > last) parts.push(<span key={parts.length}>{line.slice(last, m.index)}</span>);
        const [whole, ws, str, kw, num] = m;
        if (ws) parts.push(<span key={parts.length}>{whole}</span>);
        else if (str) parts.push(<span key={parts.length} className="text-accent-mint">{whole}</span>);
        else if (kw) parts.push(<span key={parts.length} className="text-accent-cyan">{whole}</span>);
        else if (num) parts.push(<span key={parts.length} className="text-[#FFD180]">{whole}</span>);
        last = m.index + whole.length;
    }
    if (last < line.length) parts.push(<span key={parts.length}>{line.slice(last)}</span>);
    return parts.length ? parts : line;
}

export const LiveCodePreview = ({ payload, cliType, vendor, model, hostname }) => {
    const [open, setOpen] = useState(true);

    const { script, error } = useMemo(() => {
        if (!cliType) return { script: "", error: null };
        try {
            const fn = getGenerator(cliType);
            const s = fn(payload || {});
            return { script: typeof s === "string" ? s : String(s ?? ""), error: null };
        } catch (e) {
            return { script: "", error: e?.message || String(e) };
        }
    }, [payload, cliType]);

    const filename = useMemo(
        () => getOutputFilename(cliType || "txt", hostname || "", model || ""),
        [cliType, hostname, model],
    );

    const onCopy = useCallback(() => {
        if (!script) return;
        navigator.clipboard.writeText(script).then(
            () => toast.success("Copied to clipboard"),
            () => toast.error("Copy failed"),
        );
    }, [script]);

    const lines = (script || "").split("\n");
    const hasContent = lines.some((l) => l.trim());

    return (
        <AnimatePresence initial={false}>
            {open ? (
                <motion.aside
                    key="open"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 480, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.24, ease: "easeOut" }}
                    className="shrink-0 border-l border-white/[0.05] bg-canvas overflow-hidden"
                    data-testid="live-preview"
                >
                    <div className="h-full flex flex-col">
                        {/* Window chrome */}
                        <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/[0.05] bg-[#0D0D0D]">
                            <div className="flex items-center gap-2">
                                <span className="flex gap-1.5">
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
                                    <span className="w-2.5 h-2.5 rounded-full bg-[#27C93F]" />
                                </span>
                                <span className="flex items-center gap-1.5 ml-2 text-[11px] font-mono text-text-muted">
                                    <FileCode2 className="w-3 h-3" />
                                    <span data-testid="live-preview-filename">{filename}</span>
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="text-text-dim hover:text-text-primary transition-colors p-1"
                                aria-label="Collapse preview"
                                data-testid="live-preview-collapse"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Code body */}
                        <div className="flex-1 overflow-auto bg-[#0D0D0D]">
                            {error ? (
                                <div className="p-5 text-[12.5px] text-accent-amber font-mono">
                                    Configuration preview will populate as you fill the form.
                                    <div className="mt-2 text-[10.5px] text-text-dim">[{error}]</div>
                                </div>
                            ) : hasContent ? (
                                <pre
                                    className="text-[12px] leading-[1.55] font-mono px-4 py-3 text-text-primary whitespace-pre"
                                    data-testid="live-preview-code"
                                >
                                    {lines.map((line, i) => (
                                        <div key={i} className="flex">
                                            <span className="inline-block w-9 pr-2 text-right text-text-dim/60 select-none">
                                                {i + 1}
                                            </span>
                                            <span className="flex-1">{highlight(line || " ")}</span>
                                        </div>
                                    ))}
                                </pre>
                            ) : (
                                <div className="p-5 text-[12.5px] text-text-muted font-mono">
                                    Configuration preview will populate as you fill the form.
                                </div>
                            )}
                        </div>

                        {/* Footer actions */}
                        <div className="flex items-center justify-between px-3 py-2 border-t border-white/[0.05] bg-[#0D0D0D]">
                            <div className="text-[10.5px] font-mono text-text-dim">
                                {hasContent ? `${lines.length} lines` : "Empty"}
                            </div>
                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={onCopy}
                                    disabled={!hasContent}
                                    className="text-text-dim hover:text-accent-cyan disabled:opacity-30 disabled:cursor-not-allowed transition-colors p-1.5 rounded hover:bg-white/[0.04]"
                                    aria-label="Copy"
                                    data-testid="live-preview-copy"
                                >
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    type="button"
                                    disabled
                                    className="text-text-dim opacity-30 cursor-not-allowed p-1.5 rounded"
                                    aria-label="Download (available at Step 7)"
                                    data-testid="live-preview-download"
                                    title="Download available at Step 7"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.aside>
            ) : (
                <motion.button
                    key="closed"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    type="button"
                    onClick={() => setOpen(true)}
                    className="shrink-0 w-9 border-l border-white/[0.05] bg-canvas hover:bg-white/[0.02] flex flex-col items-center justify-center gap-3 text-text-muted hover:text-accent-cyan transition-colors"
                    data-testid="live-preview-expand"
                    aria-label="Expand preview"
                >
                    <ChevronLeft className="w-4 h-4" />
                    <span
                        className="text-[10.5px] font-mono uppercase tracking-[0.22em]"
                        style={{ writingMode: "vertical-rl" }}
                    >
                        Live CLI
                    </span>
                </motion.button>
            )}
        </AnimatePresence>
    );
};

export default LiveCodePreview;
